'use client';
import { useState, ReactNode, useRef, useMemo, useId } from 'react';
import { PlanCard, PlanCardProps } from '@/components/atoms/PlanCard';
import { Button, ButtonProps } from '@/ui-components/atoms/Button/Button';
import { InvalidFeedback } from '@/ui-components/atoms/Inputs/InvalidFeedback/InvalidFeedback';
import useInheritParams from '@/hooks/useInheritParams';
import { URL_INHERIT_PARAMS, VALIDITY } from '@/constants';
import styles from './planselector.module.scss';

const {
    mainWrapper,
    selectorWrapper,
    offerSection,
    planCardWrapper,
    inputStyles,
    offerTitleStyles,
    offerPriceStyles,
    errorMessageStyles,
    headlineStyles,
    offerDetailStyles,
    selectButtonStyles,
    textLinkStyles,
    inputWrapper,
    textContentWrapper,
} = styles;

type DefaultCtaData = {
    href: string;
    linkText: string;
    ariaLabel: string;
};

type PlanData = {
    cta: DefaultCtaData;
    subHeadline: ReactNode;
    creditCardText: ReactNode;
};

interface Plans extends PlanCardProps, PlanData {}

interface PlanSelectorProps {
    mainHeadline: ReactNode;
    offerTitle?: ReactNode;
    plans: Plans[];
    defaultCtaData: DefaultCtaData;
    textLinkData?: ButtonProps;
}

export default function PlanSelector({
    mainHeadline,
    plans,
    offerTitle,
    defaultCtaData,
    textLinkData,
}: PlanSelectorProps): React.JSX.Element {
    const uniqueId = useId();
    const defaultPlanData = useMemo(() => {
        return {
            cta: {
                href: defaultCtaData.href,
                linkText: defaultCtaData.linkText,
                ariaLabel: defaultCtaData.ariaLabel,
            },
            subHeadline: '',
            creditCardText: '',
        };
    }, [defaultCtaData.ariaLabel, defaultCtaData.href, defaultCtaData.linkText]);

    const [selectedPlan, setSelectedPlan] = useState<PlanData>(() => {
        if (plans && plans.length === 1) {
            return {
                cta: plans[0].cta,
                subHeadline: plans[0].subHeadline,
                creditCardText: plans[0].creditCardText,
            };
        }
        return defaultPlanData;
    });
    const [errorMessage, setErrorMessage] = useState(false);
    const [selectedInput, setSelectedInput] = useState(false);
    const inputRef = useRef<(HTMLInputElement | null)[]>([]);

    const href = useInheritParams(selectedPlan.cta.href || '#', URL_INHERIT_PARAMS);

    const onChangeHandler = (index: number) => {
        if (selectedPlan.cta.linkText === plans[index].cta.linkText && !selectedInput) {
            setSelectedInput(true);
            setSelectedPlan(defaultPlanData);
            inputRef.current?.[index]?.blur();
        } else if (selectedPlan.cta.linkText === plans[index].cta.linkText && selectedInput) {
            setSelectedInput(false);
            setErrorMessage(false);
            setSelectedPlan({
                cta: plans[index].cta,
                subHeadline: plans[index].subHeadline,
                creditCardText: plans[index].creditCardText,
            });
        } else {
            setSelectedInput(false);
            setErrorMessage(false);
            setSelectedPlan({
                cta: plans[index].cta,
                subHeadline: plans[index].subHeadline,
                creditCardText: plans[index].creditCardText,
            });
        }
    };

    const handleButtonClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (selectedPlan.cta.href === '' || selectedPlan.cta.href === '?') {
            e.preventDefault();
            setErrorMessage(true);
            return;
        }

        setErrorMessage(false);
    };

    const content = plans?.map(({ headline, descriptionText, iconSource }, index: number) => {
        const isOnlyOption = plans.length === 1;
        const isSelected = isOnlyOption || selectedPlan.cta.linkText === plans[index].cta.linkText;
        const elementId = `${uniqueId}-${index}`;

        return (
            <div key={index} className={inputWrapper}>
                <input
                    id={elementId}
                    ref={(ref) => {
                        inputRef.current[index] = ref;
                    }}
                    type="radio"
                    name="select"
                    className={inputStyles}
                    onClick={() => onChangeHandler(index)}
                    aria-label={`Listen on ${headline}`}
                    data-linkname={headline}
                    checked={isSelected}
                    readOnly
                />
                <label htmlFor={elementId}>
                    <PlanCard headline={headline} descriptionText={descriptionText} iconSource={iconSource} />
                </label>
            </div>
        );
    });

    const selectedPlanText =
        selectedPlan.subHeadline !== '' || selectedPlan.creditCardText !== '' ? (
            <>
                <span>{selectedPlan.subHeadline}</span>
                <span>{selectedPlan.creditCardText}</span>
            </>
        ) : (
            <>
                Then current plan price. See <span className={offerDetailStyles}>Offer Details.</span>
            </>
        );

    return (
        <div className={mainWrapper}>
            <div className={selectorWrapper}>
                <span className={headlineStyles}>{mainHeadline}</span>

                {errorMessage ? (
                    <div className={errorMessageStyles}>
                        <InvalidFeedback validState={VALIDITY.invalid}>Please make a selection</InvalidFeedback>
                    </div>
                ) : null}
                <div className={planCardWrapper}>{content}</div>
                <div className={offerSection}>
                    <div role="status" className={textContentWrapper}>
                        {offerTitle && <span className={offerTitleStyles}>{offerTitle}</span>}
                        <span className={offerPriceStyles}>{selectedPlanText}</span>
                    </div>
                    <Button
                        className={selectButtonStyles}
                        href={href}
                        variant="primary"
                        type="button"
                        onClick={handleButtonClick}
                        ariaLabel={selectedPlan.cta.ariaLabel}
                        dataCy="plan-selector-cta"
                    >
                        {selectedPlan.cta.linkText}
                    </Button>
                    {textLinkData?.href ? (
                        <Button
                            {...textLinkData}
                            className={textLinkStyles}
                            variant="tertiary"
                            type="button"
                            tertiaryNoPadding={true}
                            fitting="fill-container"
                            inheritParams={textLinkData?.inheritParams}
                            dataCy="plan-selector-text-link"
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
}
